import { useEffect, useRef } from 'react';
import { radioStations } from '@/mocks/radios';
import type { RadioEvent } from '@/mocks/adminEvents';
import {
  eventTypeLabels, eventTypeIcons, eventTypeColors,
  eventStatusLabels,
  eventModalityLabels, eventModalityIcons,
  eventCategoryLabels, eventCategoryIcons,
} from '@/mocks/adminEvents';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface EventDetailModalProps {
  event: RadioEvent;
  onClose: () => void;
  onShare: (event: RadioEvent) => void;
  onSave: (event: RadioEvent) => void;
  onRemind: (event: RadioEvent) => void;
  isSaved: boolean;
}

export default function EventDetailModal({
  event, onClose, onShare, onSave, onRemind, isSaved,
}: EventDetailModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const station = radioStations.find((s) => s.id === event.stationId);

  const isToday = event.status === 'publicado';
  const isPast = event.status === 'finalizado';
  const isCancelled = event.status === 'cancelado';
  const hasCapacity = event.capacity > 0;
  const typeColors = eventTypeColors[event.type];
  const modalityLabel = eventModalityLabels[event.modality];
  const modalityIcon = eventModalityIcons[event.modality];

  const dateObj = new Date(event.date);
  const day = dateObj.getDate();
  const month = dateObj.toLocaleDateString('es-MX', { month: 'long' });
  const year = dateObj.getFullYear();
  const weekday = dateObj.toLocaleDateString('es-MX', { weekday: 'long' });

  const mapsQuery = encodeURIComponent(`${event.location}`);

  const modalityStyle = event.modality === 'presencial' ? 'bg-amber-50 text-amber-700 border-amber-300' : event.modality === 'online' ? 'bg-sky-50 text-sky-700 border-sky-300' : 'bg-violet-50 text-violet-700 border-violet-300';

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const isValidEvent = !isPast && !isCancelled;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-10 px-4"
    >
      <div className="relative w-full max-w-[900px] bg-background-50 rounded-3xl overflow-hidden my-auto animate-[fadeIn_0.2s_ease-out]">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-background-50/80 backdrop-blur-sm border border-background-200 flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-foreground-600 text-lg"></i>
        </button>

        {/* Hero image */}
        <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={event.image}
            alt={event.title}
            className={`w-full h-full object-cover object-top ${isPast ? 'grayscale-[30%]' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>

          {/* Date badge */}
          <div className={`absolute top-4 left-4 w-16 h-16 rounded-xl flex flex-col items-center justify-center shrink-0 ${
            isToday ? 'bg-red-500 text-background-50' :
            isCancelled ? 'bg-red-500 text-background-50' :
            isPast ? 'bg-foreground-400 text-background-50' :
            'bg-background-50 text-foreground-950'
          }`}>
            <span className="text-[10px] font-bold uppercase leading-none">{month.slice(0,3)}</span>
            <span className="text-xl font-extrabold leading-none">{day}</span>
          </div>

          {/* Status badge */}
          <div className={`absolute top-4 right-16 px-3 py-1.5 rounded-full text-[10px] font-bold ${
            isToday ? 'bg-accent-500 text-background-50' :
            isCancelled ? 'bg-red-500 text-background-50' :
            isPast ? 'bg-foreground-300 text-foreground-700' :
            'bg-secondary-500 text-background-50'
          }`}>
            {eventStatusLabels[event.status]}
          </div>

          {/* Type chip */}
          <div className={`absolute top-14 right-16 px-2 py-0.5 rounded-full text-[9px] font-semibold ${typeColors.bg} ${typeColors.text} border ${typeColors.border}`}>
            <i className={`${eventTypeIcons[event.type]} text-[7px] mr-1`}></i>
            {eventTypeLabels[event.type]}
          </div>

          {/* Title overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <i className={`${eventCategoryIcons[event.category]} text-xs text-background-50/80`}></i>
              <span className="text-[10px] font-bold text-background-50/80 uppercase tracking-wider">{eventCategoryLabels[event.category]}</span>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${modalityStyle}`}>
                <i className={`${modalityIcon} text-[8px]`}></i>
                {modalityLabel}
              </span>
            </div>
            <h2 className="text-xl md:text-2xl font-extrabold text-background-50 leading-tight">{event.title}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 md:p-6 space-y-5">
          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-3 md:gap-5 text-xs font-medium text-foreground-600">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-calendar-line text-foreground-400 text-[11px]"></i>
              </div>
              <span className="capitalize">{weekday}, {day} de {month} de {year}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-time-line text-foreground-400 text-[11px]"></i>
              </div>
              <span>{event.time}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-map-pin-line text-foreground-400 text-[11px]"></i>
              </div>
              <span>{event.location}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-background-100 flex items-center justify-center">
                <i className="ri-group-line text-foreground-400 text-[11px]"></i>
              </div>
              <span>{event.participants.toLocaleString()} participantes</span>
            </div>
            {hasCapacity && (
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-lg bg-background-100 flex items-center justify-center">
                  <i className="ri-user-line text-foreground-400 text-[11px]"></i>
                </div>
                <span>Cupo: {event.capacity.toLocaleString()}</span>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <h4 className="text-sm font-extrabold text-foreground-950 mb-2">Acerca del evento</h4>
            <p className="text-sm text-foreground-600 leading-relaxed">{event.description}</p>
          </div>

          {/* Modality info */}
          {event.modality === 'online' && (
            <div className="p-4 bg-sky-50 border border-sky-200 rounded-2xl flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                <i className="ri-global-line text-sky-600 text-base"></i>
              </div>
              <div>
                <h5 className="text-xs font-bold text-sky-800 mb-1">Evento Online</h5>
                <p className="text-[11px] text-sky-600 leading-relaxed">Este evento se realizará de forma virtual. Recibirás el enlace de acceso después de confirmar tu asistencia.</p>
              </div>
            </div>
          )}
          {event.modality === 'hibrido' && (
            <div className="p-4 bg-violet-50 border border-violet-200 rounded-2xl flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                <i className="ri-smartphone-line text-violet-600 text-base"></i>
              </div>
              <div>
                <h5 className="text-xs font-bold text-violet-800 mb-1">Evento Híbrido</h5>
                <p className="text-[11px] text-violet-600 leading-relaxed">Podés asistir de forma presencial o conectarte de manera virtual. Elegí la opción que prefieras al confirmar.</p>
              </div>
            </div>
          )}

          {/* Location + Map (only for presencial/híbrido) */}
          {event.modality !== 'online' && (
            <div>
              <h4 className="text-sm font-extrabold text-foreground-950 mb-2">Ubicación</h4>
              <p className="text-xs text-foreground-500 mb-3">{event.location}</p>
              <div className="w-full h-[220px] rounded-2xl overflow-hidden border border-background-200">
                <iframe
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zEkVBtLg3zCOOgHvEFkIFcDU&q=${mapsQuery}&language=es&zoom=14`}
                  title={`Mapa de ${event.title}`}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}

          {/* Station info */}
          {station && (
            <div className="flex items-center gap-3 p-4 bg-background-100 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-background-200 overflow-hidden shrink-0">
                <img src={station.logo} alt={station.name} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-foreground-400">Organizado por</p>
                <p className="text-sm font-bold text-foreground-950">{station.name}</p>
              </div>
              <span className="text-[10px] font-medium text-foreground-400 bg-background-200 rounded-lg px-2.5 py-1">{station.frequency} RFW</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-background-100 text-center">
              <p className="text-lg font-extrabold text-foreground-950">{event.participants.toLocaleString()}</p>
              <p className="text-[10px] font-semibold text-foreground-400">Participantes</p>
            </div>
            <div className="p-3 rounded-xl bg-background-100 text-center">
              <p className="text-lg font-extrabold text-foreground-950">{event.interested.toLocaleString()}</p>
              <p className="text-[10px] font-semibold text-foreground-400">Interesados</p>
            </div>
            <div className="p-3 rounded-xl bg-background-100 text-center">
              <p className="text-lg font-extrabold text-foreground-950">{event.shares.toLocaleString()}</p>
              <p className="text-[10px] font-semibold text-foreground-400">Compartidos</p>
            </div>
            <div className="p-3 rounded-xl bg-background-100 text-center">
              <p className="text-lg font-extrabold text-foreground-950">{event.followers.toLocaleString()}</p>
              <p className="text-[10px] font-semibold text-foreground-400">Seguidores</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2.5 pt-2 border-t border-background-100">
            <button
              onClick={() => onShare(event)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200 text-foreground-700 rounded-xl text-xs font-bold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line text-sm"></i>
              Compartir
            </button>
            <button
              onClick={() => onSave(event)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border rounded-xl text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                isSaved
                  ? 'bg-secondary-500 text-background-50 border-secondary-500'
                  : 'bg-background-100 border-background-200 text-foreground-700 hover:bg-background-200'
              }`}
            >
              <i className={`${isSaved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-sm`}></i>
              {isSaved ? 'Guardado' : 'Guardar'}
            </button>
            {isValidEvent && (
              <button
                onClick={() => onRemind(event)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 border border-primary-500 rounded-xl text-xs font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-notification-3-line text-sm"></i>
                Recordarme
              </button>
            )}
            {isCancelled && (
              <span className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs font-bold">
                <i className="ri-close-circle-line text-sm"></i>
                Evento Cancelado
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}